import axios from 'axios';

const api = axios.create({
  // Same-origin in production (backend serves the frontend); localhost in dev
  baseURL: process.env.REACT_APP_API_URL
    || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001')
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('pbc_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A 401 on any normal call means the session is over — expired, or ended
// because the password was changed somewhere else — so the token is thrown
// away and the browser goes back to the login screen.
//
// EXCEPT on the login call itself. A wrong email or password is also a 401,
// and sending that one to /login reloads the whole page, which wipes the
// "Invalid email or password" the login form had just put on screen. The
// result was a mistyped password looking like nothing happened at all. That
// call is let through so the login page can show its own message.
//
// (Found while testing the change-password work: a coach coming back in with
// their new password and fat-fingering it got a blank screen and no reason.)
const isLoginRequest = (err) => (err.config?.url || '').includes('/api/auth/login');

// The server says WHY the session ended — the password was changed somewhere
// else, or the account has been deactivated. That sentence used to be thrown
// away by the redirect on the line below, so a coach who had been taken off
// the books was simply dumped on the login screen with no explanation, typed
// their password, and was told it was wrong. Now the reason is carried across
// the redirect and shown on the login page.
//
// sessionStorage, not localStorage: it should explain THIS sign-out and then
// be gone, not resurface next week when the browser is reopened.
export const SIGNED_OUT_REASON_KEY = 'pbc_signed_out_reason';

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !isLoginRequest(err)) {
      localStorage.removeItem('pbc_token');
      localStorage.removeItem('pbc_user');
      const reason = err.response?.data?.error;
      try {
        if (reason) sessionStorage.setItem(SIGNED_OUT_REASON_KEY, reason);
      } catch {
        // A browser with storage blocked still gets signed out properly,
        // it just does not get the explanation. Never let this throw.
      }
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;
